import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { YEAR_CONSTRAINTS } from '../constants';

function GenreFilter({ genres, selectedGenre, onGenreChange }) {
  const handleChange = (e) => {
    onGenreChange(e.target.value);
  };

  return (
    <select
      value={selectedGenre}
      onChange={handleChange}
      className="input-primary text-sm flex-1 sm:flex-none sm:w-[180px]"
    >
      {genres.map((genre) => (
        <option key={genre.id} value={genre.id}>{genre.name}</option>
      ))}
    </select>
  );
}

function SortFilter({ sortOptions, sortBy, onSortChange }) {
  const handleChange = (e) => {
    onSortChange(e.target.value);
  };

  return (
    <select
      value={sortBy}
      onChange={handleChange}
      className="input-primary text-sm flex-1 sm:flex-none sm:w-[180px]"
    >
      {sortOptions.map((sort) => (
        <option key={sort.value} value={sort.value}>{sort.label}</option>
      ))}
    </select>
  );
}

function RatingFilter({ 
  label, 
  value, 
  onChange, 
  min = 0, 
  max = 10, 
  step = 0.1,
  icon = null
}) {
  const [localValue, setLocalValue] = useState(value);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleSliderChange = (e) => {
    const newValue = parseFloat(e.target.value);
    setLocalValue(newValue);
    onChange(newValue, true);
  };

  const handleSliderStart = () => setIsDragging(true);
  const handleSliderEnd = () => setIsDragging(false);

  const handleKeyDown = (e) => {
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) {
      const input = e.target;
      requestAnimationFrame(() => {
        const newValue = parseFloat(input.value);
        setLocalValue(newValue);
        onChange(newValue);
      });
    }
  };

  return (
    <div className="flex flex-col space-y-2 min-w-[160px]">
      <label className="text-xs text-gray-400 flex items-center space-x-1">
        {icon}
        <span>{label}</span>
      </label>
      <div className="flex items-center space-x-2">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={localValue}
          onChange={handleSliderChange}
          onMouseDown={handleSliderStart}
          onMouseUp={handleSliderEnd}
          onTouchStart={handleSliderStart}
          onTouchEnd={handleSliderEnd}
          onKeyDown={handleKeyDown}
          className={`flex-1 accent-primary-500 cursor-pointer transition-all ${
            isDragging ? 'cursor-grabbing scale-105' : 'cursor-grab hover:scale-102'
          }`}
          title={`${label}: ${localValue}${max === 100 ? '%' : ''} ir daugiau`}
        />
        <span className={`text-sm min-w-[40px] text-center transition-colors ${
          isDragging ? 'text-primary-400 font-medium' : 'text-white'
        }`}>
          {localValue}{max === 100 ? '%' : ''}+
        </span>
      </div>
    </div>
  );
}

function YearFilter({ fromYear, toYear, onFromYearChange, onToYearChange }) {
  const currentYear = YEAR_CONSTRAINTS.CURRENT_YEAR;
  const minYear = YEAR_CONSTRAINTS.MIN_YEAR;

  const generateYearOptions = (startYear, endYear) => {
    return Array.from({ length: endYear - startYear + 1 }, (_, i) => {
      const year = endYear - i;
      return (
        <option key={year} value={year}>{year}</option>
      );
    });
  };

  const handleFromYearChange = (value) => {
    onFromYearChange(value);
    if (toYear && value && parseInt(toYear) < parseInt(value)) {
      onToYearChange('');
    }
  };

  const getToYearMinimum = () => {
    return fromYear ? Math.max(parseInt(fromYear), minYear) : minYear;
  };

  return (
    <div className="flex flex-col space-y-2 min-w-[180px]">
      <label className="text-xs text-gray-400 flex items-center space-x-1">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span>Išleidimo metai</span>
      </label>
      <div className="flex items-center space-x-2">
        <select
          value={fromYear}
          onChange={(e) => handleFromYearChange(e.target.value)}
          className="input-primary text-xs flex-1"
          title="Pasirinkite nuo kurių metų filtruoti (nuo 1990)"
        >
          <option value="">Nuo</option>
          {generateYearOptions(minYear, currentYear)}
        </select>
        
        <span className="text-gray-400">-</span>
        
        <select
          value={toYear}
          onChange={(e) => onToYearChange(e.target.value)}
          className="input-primary text-xs flex-1"
          title={fromYear 
            ? `Pasirinkite iki kurių metų filtruoti (nuo ${fromYear})` 
            : "Pasirinkite iki kurių metų filtruoti (nuo 1990)"
          }
        >
          <option value="">Iki</option>
          {generateYearOptions(getToYearMinimum(), currentYear)}
        </select>
      </div>
      
      {fromYear && (
        <div className="text-xs text-gray-500 mt-1">
          Rodomi metai nuo {fromYear} iki {currentYear}
        </div>
      )}
    </div>
  );
}

function ViewModeToggle({ viewMode, onViewModeChange }) {
  return (
    <div className="hidden sm:flex bg-dark-700 rounded-lg p-1">
      <button 
        onClick={() => onViewModeChange('grid')}
        className={`p-2 rounded-md transition-colors ${
          viewMode === 'grid' 
            ? 'text-primary-400 bg-dark-600' 
            : 'text-gray-400 hover:text-white hover:bg-dark-600'
        }`} 
        title="Tinklelio vaizdas"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      </button>
      <button 
        onClick={() => onViewModeChange('list')}
        className={`p-2 rounded-md transition-colors ${
          viewMode === 'list' 
            ? 'text-primary-400 bg-dark-600' 
            : 'text-gray-400 hover:text-white hover:bg-dark-600'
        }`} 
        title="Sąrašo vaizdas"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      </button>
    </div>
  );
}

function MediaFilters({
  mediaType = 'movie',
  title = 'Populiarūs filmai',
  subtitle = '',
  filters,
  onFiltersChange,
  viewMode,
  onViewModeChange,
  genres = [],
  sortOptions = [],
  hideTitle = false,
  hideAvailableLT = false,
  resetFilters,
  hasActiveFilters = false,
  resetViewMode
}) {
  const navigate = useNavigate();
  const [draftRatings, setDraftRatings] = useState(() => ({
    imdbRating: filters.imdbRating,
    rottenTomatoesRating: filters.rottenTomatoesRating
  }));
  const [draftYears, setDraftYears] = useState(() => ({
    fromYear: filters.fromYear,
    toYear: filters.toYear
  }));

  useEffect(() => {
    setDraftRatings({
      imdbRating: filters.imdbRating,
      rottenTomatoesRating: filters.rottenTomatoesRating
    });
  }, [filters.imdbRating, filters.rottenTomatoesRating]);

  useEffect(() => {
    setDraftYears({
      fromYear: filters.fromYear,
      toYear: filters.toYear
    });
  }, [filters.fromYear, filters.toYear]);

  const handleFilterChange = (key, value) => {
    onFiltersChange({ [key]: value });
  };

  const handleSortChange = (newSortBy) => {
    handleFilterChange('sortBy', newSortBy);
  };

  const applyRatingFilters = (next) => {
    onFiltersChange(next);
  };

  const applyYearFilters = (next) => {
    onFiltersChange(next);
  };

  const handleRatingChange = (key, value) => {
    setDraftRatings(prev => {
      const next = { ...prev, [key]: value };
      // Only send the changed field to avoid race conditions with debounced updates
      applyRatingFilters({ [key]: value });
      return next;
    });
  };

  const handleFromYearChange = (value) => {
    setDraftYears(prev => {
      const next = { ...prev, fromYear: value };
      // Only send the changed field, let the hook handle validation and toYear adjustment
      applyYearFilters({ fromYear: value });
      return next;
    });
  };

  const handleToYearChange = (value) => {
    setDraftYears(prev => {
      const next = { ...prev, toYear: value };
      // Only send the changed field, let the hook handle validation and fromYear adjustment
      applyYearFilters({ toYear: value });
      return next;
    });
  };

  return (
    <section className="sticky top-[73px] z-40 backdrop-blur-xl border-b border-dark-700/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {!hideTitle && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6">
                <h2 className="text-xl font-semibold text-white">
                  {mediaType === 'movie' ? (
                    <>
                      <span className="text-primary-400">Filmai</span>
                      <span className="text-gray-500 mx-2">/</span>
                      <button
                        onClick={() => navigate('/tv')}
                        className="text-gray-400 hover:text-white transition-colors hover:underline"
                      >
                        TV Serialai
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => navigate('/')}
                        className="text-gray-400 hover:text-white transition-colors hover:underline"
                      >
                        Filmai
                      </button>
                      <span className="text-gray-500 mx-2">/</span>
                      <span className="text-primary-400">TV Serialai</span>
                    </>
                  )}
                </h2>
                {subtitle && (
                  <div className="text-sm text-gray-400 hidden lg:block">
                    {subtitle}
                  </div>
                )}
              </div>
            )}
            {hideTitle && subtitle && (
              <div className="text-sm text-gray-400">
                {subtitle}
              </div>
            )}
            
            <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-4">
              <GenreFilter
                genres={genres}
                selectedGenre={filters.selectedGenre}
                onGenreChange={(value) => handleFilterChange('selectedGenre', value)}
              />

              <SortFilter
                sortOptions={sortOptions}
                sortBy={filters.sortBy}
                onSortChange={handleSortChange}
              />

              <ViewModeToggle
                viewMode={viewMode}
                onViewModeChange={onViewModeChange}
              />

              {hasActiveFilters && resetFilters && (
                <button
                  onClick={() => {
                    resetFilters();
                    if (resetViewMode) {
                      resetViewMode('grid');
                    }
                  }}
                  className="px-3 py-2 text-sm font-medium text-gray-300 bg-dark-700 hover:bg-dark-600 border border-dark-600 hover:border-dark-500 rounded-lg transition-colors flex items-center gap-2"
                  title="Atstatyti filtrus"
                  aria-label="Atstatyti filtrus"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span className="hidden sm:inline">Atstatyti filtrus</span>
                </button>
              )}
            </div>
          </div>

          <div className="border-t border-dark-700 pt-4">
            <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${hideAvailableLT ? 'lg:grid-cols-3' : 'lg:grid-cols-4'}`}>
              <RatingFilter
                label="IMDB reitingas"
                value={draftRatings.imdbRating}
                onChange={(value) => handleRatingChange('imdbRating', value)}
                min={0}
                max={10}
                step={0.1}
                icon={
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                }
              />

              <RatingFilter
                label="Rotten Tomatoes"
                value={draftRatings.rottenTomatoesRating}
                onChange={(value) => handleRatingChange('rottenTomatoesRating', value)}
                min={0}
                max={100}
                step={1}
                icon={
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                  </svg>
                }
              />

              <YearFilter
                fromYear={draftYears.fromYear}
                toYear={draftYears.toYear}
                onFromYearChange={handleFromYearChange}
                onToYearChange={handleToYearChange}
              />

              {!hideAvailableLT && (
              <div className="flex flex-col space-y-2 min-w-[180px]">
                <label className="text-xs text-gray-400 flex items-center space-x-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Prieinamumas</span>
                </label>
                <div className="flex items-center space-x-3">
                  <div className="relative flex items-center">
                    <input
                      type="checkbox"
                      id="availableLT"
                      className="sr-only"
                      checked={filters.availableLT === 'true'}
                      onChange={(e) => handleFilterChange('availableLT', e.target.checked ? 'true' : 'false')}
                    />
                    <label 
                      htmlFor="availableLT" 
                      className={`
                        flex items-center space-x-2 cursor-pointer select-none
                        px-3 py-2 rounded-lg border transition-all duration-200
                        ${filters.availableLT === 'true' 
                          ? 'bg-primary-600/20 border-primary-500 text-primary-400' 
                          : 'bg-dark-700/50 border-dark-600 text-gray-300 hover:border-dark-500 hover:bg-dark-700'
                        }
                      `}
                    >
                      <div className={`
                        w-4 h-4 rounded border-2 flex items-center justify-center transition-all duration-200
                        ${filters.availableLT === 'true'
                          ? 'bg-primary-500 border-primary-500'
                          : 'border-gray-500 bg-transparent'
                        }
                      `}>
                        {filters.availableLT === 'true' && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm font-medium">Rodyti tik prieinamus Lietuvoje</span>
                    </label>
                  </div>
                </div>
              </div>
              )}
            </div>

            {(draftRatings.imdbRating > 0 || draftRatings.rottenTomatoesRating > 0 || draftYears.fromYear || draftYears.toYear) && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-400">Aktyvūs filtrai:</span>
                
                {draftRatings.imdbRating > 0 && (
                  <span className="px-3 py-1 rounded-full text-sm border bg-primary-600/20 text-primary-300 border-primary-500/30">
                    IMDB: {draftRatings.imdbRating}+
                  </span>
                )}
                
                {draftRatings.rottenTomatoesRating > 0 && (
                  <span className="px-3 py-1 rounded-full text-sm border bg-red-600/20 text-red-300 border-red-500/30">
                    RT: {draftRatings.rottenTomatoesRating}%+
                  </span>
                )}
                
                {(draftYears.fromYear || draftYears.toYear) && (
                  <span className="px-3 py-1 rounded-full text-sm border bg-blue-600/20 text-blue-300 border-blue-500/30">
                    📅 {draftYears.fromYear || YEAR_CONSTRAINTS.MIN_YEAR} - {draftYears.toYear || YEAR_CONSTRAINTS.CURRENT_YEAR}
                  </span>
                )}

                {!hideAvailableLT && filters.availableLT === 'true' && (
                  <span className="px-3 py-1 rounded-full text-sm border bg-green-600/20 text-green-300 border-green-500/30">
                    🇱🇹 LT
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default MediaFilters; 